import { Trash2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface DangerZoneProps {
  /** Card heading, e.g. t("dangerZone.title") — "Danger zone". */
  title: string;
  /** Bold one-liner naming the destructive action. */
  actionTitle: string;
  /** Muted explanation of the consequences. */
  description: string;
  /** Destructive button label. */
  buttonLabel: string;
  /** Opens the caller's confirm dialog — the zone itself never deletes. */
  onAction: () => void;
  disabled?: boolean;
  className?: string;
}

/**
 * DangerZone — the standard destructive-action section for detail surfaces
 * and edit modals: a destructive-bordered card with one explained action.
 * Pair it with a confirm dialog owned by the caller (see
 * `settings/DangerZoneCard` for the org flavor, `CreatePlaceDialog` edit
 * mode for a fullscreen-modal placement at the bottom of the form).
 */
export function DangerZone({
  title,
  actionTitle,
  description,
  buttonLabel,
  onAction,
  disabled,
  className,
}: DangerZoneProps) {
  return (
    <Card className={cn("border-destructive/50", className)}>
      <CardHeader>
        <CardTitle className="text-destructive">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium">{actionTitle}</p>
            <p className="text-sm text-muted-foreground">{description}</p>
          </div>
          <Button
            type="button"
            variant="destructive"
            onClick={onAction}
            disabled={disabled}
            className="ml-4 shrink-0"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            {buttonLabel}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
